<script lang="ts" context="module">
  export const signModal = new ModalState<'sign' | 'up' | 'in'>('sign');
</script>

<script lang="ts">
  import Close from '~icons/mdi/close';
  import Send from '~icons/mdi/send';
  import Magic from '~icons/mdi/auto-fix';
  import Account from '~icons/mdi/account';

  import ActionButton from '~/components/ActionButton.svelte';
  import Modal from '~/components/Modal.svelte';
  import { signinMutation } from './mutations.svelte';
  import { page } from '$app/state';
  import { ModalState } from '~/lib/nav.svelte';
  import { resetPasswordModal } from './ResetPassword.svelte';
  import { verifyEmailModal } from './Verification.svelte';
  import ValidationError from './ValidationError.svelte';
  import LockIcon from '~icons/feather/lock';
  import LogInIcon from '~icons/feather/log-in';
  import { isApiError } from '~/types';
  import LinkButton from '~/components/LinkButton.svelte';
  import Title from '~/Title.svelte';
  import { magicLinkModal } from './MagicLink.svelte';
  import OauthButtons from './OauthButtons.svelte';
  import { toast } from 'svelte-sonner';

  const signin = signinMutation();
  let emailRef: HTMLInputElement;
  let passwordRef: HTMLElement;
  let validationError: string | undefined = undefined;

  const handleClose = () => {
    signin.reset();
    validationError = undefined;
    signModal.pop(true);
  };

  const handleSignup = () => {
    signModal.replace('up');
  };

  const handleSignin = async (e: SubmitEvent) => {
    emailRef.blur();
    passwordRef.blur();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    try {
      await signin.mutateAsync({
        email,
        password,
      });
      handleClose();
      toast.success('logged in')
    } catch (error) {
      if (isApiError(error)) {
        if (error.message === 'Email not confirmed') {
          verifyEmailModal.push(emailRef.value);
          signin.reset();
        } else {
          validationError = error.message;
        }
      }
    }
  };

  const handleReset = () => {
    resetPasswordModal.push(emailRef.value);
  };

  const handleMagicLink = () => {
    magicLinkModal.push(emailRef.value);
  };

  const handleFocus = () => {
    if (signin.error) {
      signin.reset();
    }
  };
</script>

<Modal
  anchor="body"
  open={signModal.value === 'in'}
  onClose={handleClose}
  index={6}
>
  {#snippet title()}
    <div class="title">
      <Title />
      <div><LogInIcon />Log in</div>
    </div>
  {/snippet}
  {#snippet content()}
    <div class="content">
      <OauthButtons />
      <form
        id="signin-form"
        class="auth-form"
        method="post"
        on:submit|preventDefault={handleSignin}
      >
        <ValidationError error={signin.error?.toString() ?? validationError ?? ''} />
        <input
          type="email"
          name="email"
          placeholder="email"
          class:invalid={!!(signin.error || validationError)}
          bind:this={emailRef}
          on:focus={handleFocus}
        />
        <input
          type="password"
          name="password"
          placeholder="password"
          class:invalid={!!(signin.error || validationError)}
          bind:this={passwordRef}
          on:focus={handleFocus}
        />
      </form>
      <div class="links">
        <LinkButton onClick={handleSignup}>
          don't have an account?
          <span class="gap"></span>
          <Account />Create account
        </LinkButton>
        <LinkButton onClick={handleReset}>
          forgot your password?
          <span class="gap"></span>
          <LockIcon />Reset password
        </LinkButton>
        <LinkButton onClick={handleMagicLink}>
          login with a link?
          <span class="gap"></span>
          <Magic />Send magic link
        </LinkButton>
      </div>
    </div>
  {/snippet}
  {#snippet controls()}
    <div class="controls">
      <div class="signin-container">
        <ActionButton onClick={handleClose}><Close />Back</ActionButton>
        <ActionButton
          form="signin-form"
          type="submit"
          disabled={!(signin.isIdle || signin.isSuccess) || !!page.data.user}
          ><Send />
          {#if signin.isPending}
            Logging In...
          {:else if signin.isSuccess && page.data.user}
            Logged in
          {:else}
            Log in
          {/if}
        </ActionButton>
      </div>
      <p class="agree">
        by continuing you agree to our <a href="/terms">Terms of Service </a>
        and <a href="/privacy">Privacy Policy</a>
      </p>
    </div>
  {/snippet}
</Modal>

<style lang="scss">
  @use '~/theme/theme';
  @use '~/theme/themify' as *;
  @use 'sass:color';

  .title {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5em;
    div {
      display: flex;
      align-items: center;
      gap: 0.5em;
    }
  }
  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1em;
  }
  .agree {
    padding-top: 0.5em;
    font-size: 0.75em;
    text-align: center;
    margin: 0;
    a {
      @include themify {
        color: themed('link');
      }
    }
  }
  .signin-container {
    display: grid;
    grid-auto-flow: column;
    gap: 0.5em;
  }
  .controls {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .links {
    display: flex;
    flex-direction: column;
    gap: 0.5em;
    font-size: 0.75em;
    text-align: center;
  }
  .gap {
    width: 1em;
    margin-left: auto;
  }
</style>
